/* eslint-disable */
import React, { useRef, useState, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useTheme } from '../../theme-context';
import { useAppDispatch } from '../../app/hooks';
import { tryToUploadChatFiles } from '../../store/brokers/partners/chats/ChatsSlice';

declare global {
    interface Window {
        SpeechRecognition: any;
        webkitSpeechRecognition: any;
    }
}

type SpeechRecognition = any;
type SpeechRecognitionEvent = any;

interface IChatInput {
    placeholder: string;
    chatData: any;
    chatId?: string;
    options?: any;
    stage?: string;
    considerDarkMode?: boolean;
    uploadedTemplateFiles?: any;
    setUploadedTemplateFiles?: any;
    onClickOptions?: (option: any) => void;
    onSendingData: (newChatData?: any) => void;
    isWaiting?: boolean;
    /** Rebecca in-chat composer: absolute icon slots, primary border tokens. */
    variant?: 'default' | 'rebecca';
    /** Optional width cap class for Rebecca form wrapper (e.g. `xl:w-[760px]`). */
    rebeccaFormMaxWidthClass?: string;
}

const SearchInput = ({
    placeholder,
    chatData,
    chatId,
    considerDarkMode,
    stage,
    options,
    uploadedTemplateFiles,
    setUploadedTemplateFiles,
    onClickOptions,
    onSendingData,
    isWaiting = false,
    variant = 'default',
    rebeccaFormMaxWidthClass = '',
}: IChatInput) => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const [message, setMessage] = useState('');
    const [allMessages, setAllMessages] = useState<any[]>([]);
    const [filePreviews, setFilePreviews] = useState<
        { file: File; preview?: any[][] }[]
    >([]);
    const [isListening, setIsListening] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const recognitionRef = useRef<SpeechRecognition | null>(null);

    useEffect(() => {
        setAllMessages(chatData || []);
    }, [chatData]);

    useEffect(() => {
        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.interimResults = false;
            recognition.lang = 'en-US';

            recognition.onresult = (event: SpeechRecognitionEvent) => {
                const transcript = event.results[0][0].transcript;
                setMessage((prev) => `${prev} ${transcript}`.trim());
            };

            recognition.onerror = (event: any) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
            };

            recognition.onend = () => setIsListening(false);

            recognitionRef.current = recognition;
        } else {
            console.warn(
                'Speech recognition is not supported in this browser.',
            );
        }
    }, []);

    const handleMicClick = () => {
        if (isWaiting || !recognitionRef.current) return;
        isListening
            ? recognitionRef.current.stop()
            : recognitionRef.current.start();
        setIsListening(!isListening);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const uploaded = Array.from(e.target.files);

        uploaded.forEach((file) => {
            const isCsv = file.name.endsWith('.csv');
            const isExcel =
                file.name.endsWith('.xls') || file.name.endsWith('.xlsx');
            const reader = new FileReader();

            reader.onload = (event) => {
                const result = event.target?.result;

                if (isCsv && typeof result === 'string') {
                    const parsed = Papa.parse(result, { preview: 5 })
                        .data as any[][];
                    setFilePreviews((prev) => [
                        ...prev,
                        { file, preview: parsed },
                    ]);
                } else if (isExcel && result instanceof ArrayBuffer) {
                    const wb = XLSX.read(result, { type: 'array' });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    const rows = XLSX.utils.sheet_to_json(sheet, {
                        header: 1,
                    }) as any[][];
                    setFilePreviews((prev) => [
                        ...prev,
                        { file, preview: rows.slice(0, 5) },
                    ]);
                } else {
                    setFilePreviews((prev) => [...prev, { file }]);
                }
            };

            if (isCsv) reader.readAsText(file);
            else if (isExcel) reader.readAsArrayBuffer(file);
            else setFilePreviews((prev) => [...prev, { file }]);
        });

        e.target.value = '';
    };

    const handleFileDelete = (index: number) => {
        setFilePreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        const nothingToSend =
            !message &&
            filePreviews.length === 0 &&
            (!uploadedTemplateFiles ||
                Object.values(uploadedTemplateFiles).every(
                    (files) => !Array.isArray(files) || files.length === 0,
                ));

        if (nothingToSend) return;

        let uploadedFiles: { name: string; link: string; type?: string }[] = [];

        // Upload template files
        if (
            uploadedTemplateFiles &&
            Object.keys(uploadedTemplateFiles).length > 0
        ) {
            for (const [type, files] of Object.entries(uploadedTemplateFiles)) {
                if (!Array.isArray(files) || files.length === 0) continue;

                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));
                formData.append('topicId', `${chatId}`);

                try {
                    const response = await dispatch(
                        tryToUploadChatFiles(formData),
                    ).unwrap();
                    const typedFiles = response.map(
                        (file: { name: string; link: string }) => ({
                            ...file,
                            type,
                        }),
                    );
                    uploadedFiles.push(...typedFiles);
                } catch (err) {
                    console.error(`Upload failed for type "${type}":`, err);
                }
            }
        }

        if (filePreviews.length > 0) {
            const formData = new FormData();
            filePreviews.forEach(({ file }) => formData.append('files', file));
            formData.append('topicId', `${chatId}`);

            try {
                const response = await dispatch(
                    tryToUploadChatFiles(formData),
                ).unwrap();
                uploadedFiles.push(...response);
            } catch (err) {
                console.error('Normal file upload failed:', err);
            }
        }

        if (message || uploadedFiles.length > 0) {
            const newMessage = {
                user: {
                    message,
                    sort: allMessages?.length + 2,
                    files: uploadedFiles,
                    createdAt: new Date(),
                    stage: stage,
                },
            };

            const updatedMessages = allMessages
                ? [...(allMessages || []), newMessage]
                : [newMessage];
            setAllMessages(updatedMessages);
            onSendingData(newMessage);

            setMessage('');
            setFilePreviews([]);
            if (setUploadedTemplateFiles) setUploadedTemplateFiles();

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    const rebeccaInputClass = [
        'relative z-0 min-h-[48px] w-full border-2 border-primary-default bg-white text-sm outline-none',
        'rounded-2xl py-3 px-4 pl-10 pr-20',
        'placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50',
        considerDarkMode
            ? 'dark:border-primary-darkMode dark:bg-black dark:text-primary-darkMode'
            : '',
        isWaiting ? 'border-gray-300 dark:border-gray-500' : '',
    ].join(' ');

    return (
        <div
            className={
                variant === 'rebecca' ? 'w-full' : 'min-w-full max-w-md p-2'
            }>
            <div className={variant === 'rebecca' ? 'w-full' : ''}>
                {options && options?.length > 0 && !isWaiting && (
                    <div className="flex flex-row flex-nowrap overflow-x-auto my-2 no-scrollbar px-2">
                        {options?.map((item: any, index: number) => (
                            <div
                                key={index}
                                onClick={() => onClickOptions?.(item)}
                                className="flex flex-row items-center text-sm border border-gray-300 rounded-md px-4 py-2 mr-2 shrink-0 cursor-pointer hover:opacity-80">
                                {item?.key ? (
                                    <p className="dark:text-primary-dark font-bold mr-2">
                                        {item?.key}.
                                    </p>
                                ) : (
                                    <div className="w-2 h-2 dark:bg-white rounded-full mr-2"></div>
                                )}
                                <p className="dark:text-primary-dark">
                                    {item?.value}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
                {variant === 'rebecca' ? (
                    <form
                        className={`relative mx-auto w-full max-w-full ${rebeccaFormMaxWidthClass} ${
                            isWaiting ? 'cursor-not-allowed opacity-60' : ''
                        }`.trim()}
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSend();
                        }}>
                        <div className="relative w-full">
                            <button
                                type="button"
                                onClick={handleMicClick}
                                disabled={isWaiting}
                                aria-label={
                                    isListening
                                        ? 'Stop listening'
                                        : 'Start listening'
                                }
                                className={`absolute left-3 top-1/2 z-10 -translate-y-1/2 ${
                                    isWaiting
                                        ? 'cursor-not-allowed opacity-50'
                                        : ''
                                }`}>
                                <div
                                    className={`relative h-7 w-7 ${
                                        isListening ? 'animate-pulse' : ''
                                    }`}>
                                    <img
                                        src={
                                            theme === 'dark'
                                                ? '/assets/chat/microphone-white.svg'
                                                : '/assets/chat/microphone.svg'
                                        }
                                        className="h-full w-full object-contain"
                                        alt=""
                                    />
                                    {isListening && !isWaiting && (
                                        <span className="pointer-events-none absolute inset-0 -z-10 rounded-full border-2 border-blue-400 animate-ping" />
                                    )}
                                </div>
                            </button>

                            <input
                                type="text"
                                placeholder={placeholder}
                                className={rebeccaInputClass}
                                value={message}
                                disabled={isWaiting}
                                onKeyDown={handleKeyDown}
                                onChange={(e) => setMessage(e.target.value)}
                            />

                            <input
                                type="file"
                                multiple
                                accept="*/*"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                disabled={isWaiting}
                            />

                            <button
                                type="button"
                                disabled={isWaiting}
                                onClick={() => fileInputRef.current?.click()}
                                aria-label="Attach file"
                                className={`absolute right-10 top-2.5 z-10 flex w-[25px] items-center justify-center ${
                                    isWaiting
                                        ? 'cursor-not-allowed opacity-50'
                                        : ''
                                }`}>
                                <img
                                    src={
                                        theme === 'dark'
                                            ? '/assets/chat/file-attach-white.svg'
                                            : '/assets/chat/file-attach.svg'
                                    }
                                    className="h-auto w-[25px] object-contain"
                                    alt=""
                                />
                            </button>

                            <button
                                type="submit"
                                disabled={isWaiting}
                                aria-label="Send"
                                className={`absolute right-3 top-2.5 z-10 flex w-[25px] items-center justify-center ${
                                    isWaiting
                                        ? 'cursor-not-allowed opacity-50'
                                        : ''
                                }`}>
                                <img
                                    src={
                                        theme === 'dark'
                                            ? '/assets/chat/send-white.svg'
                                            : '/assets/chat/send.svg'
                                    }
                                    className="h-auto w-[25px] object-contain"
                                    alt=""
                                />
                            </button>
                        </div>
                    </form>
                ) : (
                    <div
                        className={`flex items-center rounded-xl border-2 ${
                            isWaiting ? 'border-gray-300' : 'border-[#00330F]'
                        } bg-gradient-to-r from-white  to-blue-50 ${
                            considerDarkMode
                                ? 'dark:bg-black dark:from-black dark:border-gray-500'
                                : ''
                        } px-4 py-2 shadow-sm w-full ${
                            isWaiting ? 'opacity-60 cursor-not-allowed' : ''
                        }`}>
                        <button
                            type="button"
                            onClick={handleMicClick}
                            disabled={isWaiting}
                            aria-label={
                                isListening
                                    ? 'Stop listening'
                                    : 'Start listening'
                            }
                            className={`text-green-600 mr-2 relative ${
                                isWaiting ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                            <div
                                className={`w-[30px] h-[30px] ${
                                    isListening ? 'animate-pulse' : ''
                                }`}>
                                <img
                                    src={
                                        theme === 'dark'
                                            ? '/assets/chat/microphone-white.svg'
                                            : '/assets/chat/microphone.svg'
                                    }
                                    className="object-contain w-full h-full"
                                    alt="Mic"
                                />
                                {isListening && (
                                    <span className="absolute top-0 left-0 w-full h-full rounded-full border-2 border-blue-400 animate-ping z-[-1]" />
                                )}
                            </div>
                        </button>

                        <input
                            type="text"
                            placeholder={placeholder}
                            className={`flex-1 outline-none bg-transparent text-gray-700 ${
                                considerDarkMode ? 'dark:text-white' : ''
                            } placeholder-gray-400 disabled:cursor-not-allowed disabled:opacity-50`}
                            value={message}
                            disabled={isWaiting}
                            onKeyDown={handleKeyDown}
                            onChange={(e) => setMessage(e.target.value)}
                        />

                        <input
                            type="file"
                            multiple
                            accept="*/*"
                            className="hidden"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            disabled={isWaiting}
                        />

                        <button
                            type="button"
                            disabled={isWaiting}
                            onClick={() => fileInputRef.current?.click()}
                            className={`text-green-600 mx-2 ${
                                isWaiting ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                            <img
                                src={
                                    theme === 'dark'
                                        ? '/assets/chat/file-attach-white.svg'
                                        : '/assets/chat/file-attach.svg'
                                }
                                className="object-contain w-[30px]"
                                alt="Attach"
                            />
                        </button>

                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={isWaiting}
                            className={`text-green-600 ml-2 ${
                                isWaiting ? 'opacity-50 cursor-not-allowed' : ''
                            }`}>
                            <img
                                src={
                                    theme === 'dark'
                                        ? '/assets/chat/send-white.svg'
                                        : '/assets/chat/send.svg'
                                }
                                className="object-contain w-[30px]"
                                alt="Send"
                            />
                        </button>
                    </div>
                )}

                {filePreviews.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                        {filePreviews.map(({ file, preview }, idx) => {
                            const fileUrl = URL.createObjectURL(file);
                            const isImage = file.type?.startsWith('image/');

                            return (
                                <div
                                    key={idx}
                                    className="relative border border-gray-300 rounded p-2 w-fit max-w-[300px] bg-white">
                                    <button
                                        onClick={() => handleFileDelete(idx)}
                                        className="absolute -top-2 -right-2 bg-white text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                                        <img
                                            src={'/assets/shared/cancel.svg'}
                                            className="object-contain w-[18px]"
                                        />
                                    </button>

                                    {isImage ? (
                                        <img
                                            src={fileUrl}
                                            alt="preview"
                                            className="w-24 h-24 object-cover rounded"
                                        />
                                    ) : preview ? (
                                        <div className="overflow-x-auto max-h-32 mb-2 border">
                                            <table className="text-xs text-left border-collapse w-full">
                                                <tbody>
                                                    {preview.map(
                                                        (row, rowIdx) => (
                                                            <tr key={rowIdx}>
                                                                {row.map(
                                                                    (
                                                                        cell,
                                                                        colIdx,
                                                                    ) => (
                                                                        <td
                                                                            key={
                                                                                colIdx
                                                                            }
                                                                            className="border px-1 py-0.5 whitespace-nowrap">
                                                                            {
                                                                                cell
                                                                            }
                                                                        </td>
                                                                    ),
                                                                )}
                                                            </tr>
                                                        ),
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-600">
                                            No preview available
                                        </div>
                                    )}

                                    <div className="bg-gray-100 text-center text-xs rounded p-1 mt-1">
                                        📎 {file.name}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchInput;
